// Official AcresDeal contact (used everywhere platform contact is required)
export const PROPERTY_DEALS_CONTACT = {
  name: "AcresDeal",
  phone: "+918310281421",
  whatsapp: "+918310281421",
  display: "+91 83102 81421",
};

export const PROPERTY_TYPES = [
  "Apartment",
  "House",
  "Villa",
  "Commercial property",
  "Office",
  "Shop",
  "Warehouse",
  "Industrial property",
  "Land/plot",
  "Agricultural land",
  "Other",
];

export const TRANSACTION_TYPES = [
  { value: "sale", label: "Buy" },
  { value: "rent", label: "Rent" },
  { value: "lease", label: "Lease" },
];

// Seller-side labels for the "Post a Property" form. The underlying value
// (`sale`) stays identical to TRANSACTION_TYPES so existing DB records and
// buyer-side filters continue to work unchanged.
export const POST_PROPERTY_TRANSACTION_TYPES = [
  { value: "sale", label: "Sell" },
  { value: "rent", label: "Rent" },
  { value: "lease", label: "Lease" },
];

// Buyer-side labels for the "Post Your Requirement" form. Includes an extra
// `sell` option so users can also post a requirement to sell their property
// (distinct from `sale` which represents someone looking to buy).
export const REQUIREMENT_TRANSACTION_TYPES = [
  { value: "sale", label: "Buy" },
  { value: "sell", label: "Sell" },
  { value: "rent", label: "Rent" },
  { value: "lease", label: "Lease" },
];

// Full India-wide transaction options for filter chips (including 'Sell' as an
// alias for a listing that's for sale — from the buyer's POV it's 'Buy').
export const TRANSACTION_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "sale", label: "Buy" },
  { value: "rent", label: "Rent" },
  { value: "lease", label: "Lease" },
];

export const POSTER_TYPES = [
  { value: "owner", label: "Direct Owner / Seller", hint: "You own the property" },
  { value: "broker", label: "Broker / Agent", hint: "You represent the owner" },
];

// India-wide area units. Regional units are marked with the state cluster
// they are most commonly used in — but the ORIGINAL user unit is always
// preserved on the listing; conversion is used only for search filtering.
export const AREA_UNITS = [
  { value: "sqft", label: "sq.ft" },
  { value: "sqm", label: "sq.m" },
  { value: "sqyd", label: "sq.yd" },
  { value: "cents", label: "Cent" },
  { value: "acres", label: "Acre" },
  { value: "hectare", label: "Hectare" },
  { value: "guntha", label: "Guntha / Gunta" },
  { value: "ground", label: "Ground (TN)" },
  { value: "ankanam", label: "Ankanam (AP/KA)" },
  { value: "marla", label: "Marla (PB/HR)" },
  { value: "kanal", label: "Kanal (PB/HR)" },
  { value: "killa", label: "Killa" },
  { value: "bigha", label: "Bigha *" },
  { value: "biswa", label: "Biswa *" },
  { value: "katha", label: "Katha *" },
  { value: "dhur", label: "Dhur *" },
  { value: "decimal", label: "Decimal / Dismil" },
];

// India-wide location hierarchy — all 28 states + 8 UTs, with major cities.
// Localities are stored only for the launch region (Karnataka / Dakshina Kannada).
// Everywhere else, the Locality field is a free-text input so users can enter
// any locality, even if it is not in the predefined list.
const c = (name, localities = []) => ({ name, localities });
const st = (state, cityNames) => ({
  state,
  cities: cityNames.map((n) => (typeof n === "string" ? c(n) : n)),
});

export const ALL_INDIA = "All India";

export const INDIA_LOCATIONS = [
  {
    state: "Karnataka",
    cities: [
      c("Mangalore", [
        "Mangalore", "Kadri", "Bejai", "Kankanady", "Balmatta", "Balgmatta",
        "Hampankatta", "Bunder", "Falnir", "Kulshekar", "Kottara",
        "Bikarnakatte", "Surathkal", "Baikampady", "Panambur", "Ullal",
        "Someshwar", "Thokkottu", "Deralakatte", "Mukka", "Bondel", "Kavoor",
        "Attavar", "Bejai Kapikad", "Yeyyadi", "Padil", "Kulur", "Mangaladevi",
        "Boloor", "Pandeshwar", "Court Road", "Lalbagh", "Ashok Nagar",
        "Bejai Church Road", "Nandigudda", "Pumpwell", "Nanthur", "Marnamikatte",
        "Kudroli", "Jeppu", "Kadri Kambla", "Kadri Park", "Milagres",
      ]),
      c("Bengaluru", [
        "Whitefield", "Koramangala", "Indiranagar", "HSR Layout", "Jayanagar",
        "JP Nagar", "Electronic City", "Marathahalli", "Sarjapur Road",
        "Hebbal", "Yelahanka", "Malleshwaram", "Rajajinagar", "Basavanagudi",
        "Bannerghatta Road", "Bellandur", "Domlur", "BTM Layout", "Kalyan Nagar",
        "Banashankari", "Frazer Town", "Cox Town", "Ulsoor", "Shivaji Nagar",
        "Vijayanagar", "Rajarajeshwari Nagar", "Kengeri", "Nagarbhavi",
        "K R Puram", "Old Airport Road", "MG Road", "Brigade Road",
        "Cunningham Road", "Richmond Town", "Church Street", "Jakkur",
        "Devanahalli", "Hoodi", "ITPL", "Kadugodi",
      ]),
      c("Mysuru", ["Vijayanagar", "Kuvempunagar", "Gokulam", "Bogadi", "Hebbal", "Nazarbad"]),
      c("Hubli-Dharwad"), c("Belagavi"), c("Kalaburagi"), c("Ballari"),
      c("Vijayapura"), c("Shivamogga"), c("Tumakuru"), c("Davangere"),
      c("Udupi", ["Udupi City", "Manipal", "Malpe", "Kaup"]),
      c("Moodbidri", ["Moodbidri Town", "Belave"]),
      c("Mulki", ["Mulki Town", "Kinnigoli"]),
      c("Karkala", ["Karkala Town", "Hebri"]),
      c("Bantwal", ["Bantwal Town", "B.C. Road", "Vittal"]),
      c("Puttur", ["Puttur Town", "Uppinangady"]),
      c("Chikkamagaluru"), c("Hassan"), c("Mandya"), c("Chitradurga"),
    ],
  },
  st("Andhra Pradesh", ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Tirupati", "Kurnool", "Rajahmundry", "Kadapa", "Anantapur", "Kakinada", "Eluru", "Ongole", "Chittoor"]),
  st("Arunachal Pradesh", ["Itanagar", "Naharlagun", "Pasighat", "Tawang", "Ziro", "Bomdila"]),
  st("Assam", ["Guwahati", "Dibrugarh", "Silchar", "Jorhat", "Tezpur", "Nagaon", "Tinsukia", "Bongaigaon", "Dhubri", "Karimganj"]),
  st("Bihar", ["Patna", "Gaya", "Muzaffarpur", "Bhagalpur", "Darbhanga", "Purnia", "Bihar Sharif", "Arrah", "Begusarai", "Katihar", "Munger", "Chapra"]),
  st("Chhattisgarh", ["Raipur", "Bhilai", "Bilaspur", "Korba", "Durg", "Rajnandgaon", "Jagdalpur", "Ambikapur", "Raigarh"]),
  st("Goa", ["Panaji", "Margao", "Vasco da Gama", "Mapusa", "Ponda", "Bicholim", "Curchorem", "Calangute", "Candolim", "Anjuna"]),
  st("Gujarat", [
    c("Ahmedabad", [
      "Navrangpura", "Vastrapur", "Satellite", "Bodakdev", "Prahlad Nagar",
      "SG Highway", "Thaltej", "Bopal", "South Bopal", "Chandkheda",
      "Motera", "Naranpura", "Paldi", "Ellisbridge", "Ashram Road",
      "CG Road", "Maninagar", "Isanpur", "Vastral", "Nikol",
    ]),
    c("Surat", [
      "Adajan", "Vesu", "Piplod", "City Light", "Athwa", "Ghod Dod Road",
      "Ring Road", "Katargam", "Varachha", "Udhna", "Pandesara", "Rander",
    ]),
    "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Gandhinagar", "Junagadh",
    "Anand", "Nadiad", "Mehsana", "Bharuch", "Navsari", "Vapi", "Porbandar",
  ]),
  st("Haryana", [
    c("Gurugram", [
      "DLF Phase 1", "DLF Phase 2", "DLF Phase 3", "DLF Phase 4", "DLF Phase 5",
      "MG Road", "Golf Course Road", "Golf Course Extension Road",
      "Sohna Road", "Sector 14", "Sector 15", "Sector 29", "Sector 43",
      "Sector 47", "Sector 49", "Sector 50", "Sector 56", "Sector 57",
      "Sector 65", "Sector 66", "Sector 67", "Sector 82", "New Gurgaon",
      "Palam Vihar", "Sushant Lok", "Cyber City", "Udyog Vihar",
    ]),
    c("Faridabad", [
      "Sector 15", "Sector 21", "Sector 37", "Sector 46", "NIT Faridabad",
      "Neelam Chowk", "Old Faridabad", "Greater Faridabad", "Sector 86",
    ]),
    "Panipat", "Ambala", "Yamunanagar", "Rohtak", "Hisar", "Karnal",
    "Sonipat", "Panchkula", "Sirsa", "Bahadurgarh",
  ]),
  st("Himachal Pradesh", ["Shimla", "Dharamshala", "Manali", "Solan", "Mandi", "Kullu", "Palampur", "Baddi", "Bilaspur", "Hamirpur", "Una"]),
  st("Jharkhand", ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro Steel City", "Hazaribagh", "Deoghar", "Giridih", "Ramgarh", "Medininagar"]),
  st("Kerala", [
    c("Thiruvananthapuram", [
      "Kowdiar", "Vellayambalam", "Sasthamangalam", "Kesavadasapuram",
      "Pattom", "Vazhuthacaud", "Palayam", "Kuravankonam", "Kazhakoottam",
      "Technopark", "Medical College", "Peroorkada",
    ]),
    c("Kochi", [
      "Kakkanad", "InfoPark", "Palarivattom", "Edappally", "Kaloor",
      "Kadavanthra", "Panampilly Nagar", "Vyttila", "Thevara", "Fort Kochi",
      "Mattancherry", "Aluva", "Marine Drive", "MG Road", "Elamakkara",
    ]),
    "Kozhikode", "Thrissur", "Kollam", "Alappuzha", "Palakkad", "Kannur",
    "Kottayam", "Malappuram", "Pathanamthitta", "Kasaragod", "Idukki",
  ]),
  st("Madhya Pradesh", ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain", "Sagar", "Dewas", "Satna", "Ratlam", "Rewa", "Katni", "Singrauli", "Burhanpur", "Khandwa"]),
  st("Maharashtra", [
    c("Mumbai", [
      "Bandra West", "Bandra East", "Andheri West", "Andheri East", "Juhu",
      "Powai", "Colaba", "Fort", "Marine Lines", "Nariman Point", "Worli",
      "Lower Parel", "Prabhadevi", "Dadar", "Mahim", "Matunga", "Sion",
      "Kurla", "Ghatkopar", "Vikhroli", "Mulund", "Bhandup", "Chembur",
      "Wadala", "Byculla", "Grant Road", "Malabar Hill", "Napean Sea Road",
      "Versova", "Goregaon", "Malad", "Kandivali", "Borivali", "Dahisar",
      "Santa Cruz West", "Santa Cruz East", "Vile Parle", "Khar", "Sion Trombay",
      "Charni Road",
    ]),
    c("Pune", [
      "Koregaon Park", "Kalyani Nagar", "Viman Nagar", "Kharadi", "Hadapsar",
      "Hinjewadi", "Baner", "Balewadi", "Aundh", "Wakad", "Pimple Saudagar",
      "Pimpri", "Chinchwad", "Kothrud", "Karve Nagar", "Warje", "Bavdhan",
      "Shivaji Nagar", "Deccan Gymkhana", "Camp", "Magarpatta", "Undri",
      "NIBM Road", "Pashan", "Baner Road", "Wagholi", "Yerwada",
    ]),
    "Nagpur", "Nashik", "Thane", "Aurangabad", "Solapur", "Amravati",
    "Kolhapur", "Navi Mumbai", "Vasai-Virar", "Kalyan-Dombivli", "Sangli",
    "Jalgaon", "Akola", "Latur", "Ahmednagar", "Chandrapur", "Ratnagiri",
  ]),
  st("Manipur", ["Imphal", "Bishnupur", "Thoubal", "Ukhrul", "Churachandpur", "Senapati"]),
  st("Meghalaya", ["Shillong", "Tura", "Nongpoh", "Jowai", "Baghmara", "Williamnagar"]),
  st("Mizoram", ["Aizawl", "Lunglei", "Champhai", "Serchhip", "Kolasib", "Saiha"]),
  st("Nagaland", ["Kohima", "Dimapur", "Mokokchung", "Tuensang", "Wokha", "Zunheboto"]),
  st("Odisha", ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Sambalpur", "Puri", "Balasore", "Bhadrak", "Baripada", "Jharsuguda", "Angul"]),
  st("Punjab", ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda", "Mohali", "Pathankot", "Hoshiarpur", "Batala", "Moga", "Firozpur"]),
  st("Rajasthan", [
    c("Jaipur", [
      "Malviya Nagar", "C Scheme", "Vaishali Nagar", "Mansarovar", "Jagatpura",
      "Sanganer", "Tonk Road", "Ajmer Road", "Sikar Road", "Vidhyadhar Nagar",
      "Jhotwara", "Bani Park", "Raja Park", "Adarsh Nagar", "Civil Lines",
    ]),
    "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner", "Bhilwara", "Alwar",
    "Sri Ganganagar", "Sikar", "Pali", "Tonk", "Beawar", "Kishangarh", "Chittorgarh",
  ]),
  st("Sikkim", ["Gangtok", "Namchi", "Gyalshing", "Mangan", "Ravangla", "Rangpo"]),
  st("Tamil Nadu", [
    c("Chennai", [
      "T Nagar", "Adyar", "Anna Nagar", "Nungambakkam", "Egmore", "Mylapore",
      "Alwarpet", "Kilpauk", "Perambur", "Purasawalkam", "Chetpet",
      "Velachery", "Guindy", "Saidapet", "Ashok Nagar", "KK Nagar",
      "Vadapalani", "Porur", "Ramapuram", "Iyyapanthangal", "OMR",
      "Sholinganallur", "Perungudi", "Thoraipakkam", "Medavakkam", "Tambaram",
      "Chromepet", "Pallavaram", "Mount Road", "Royapettah",
    ]),
    c("Coimbatore", [
      "RS Puram", "Peelamedu", "Saibaba Colony", "Race Course", "Ganapathy",
      "Singanallur", "Ramanathapuram", "Puliakulam", "Ondipudur", "Vadavalli",
      "Thudiyalur", "Kovaipudur",
    ]),
    "Madurai", "Tiruchirappalli", "Salem", "Tirunelveli", "Tiruppur", "Vellore",
    "Erode", "Thoothukudi", "Dindigul", "Thanjavur", "Ranipet", "Sivakasi",
    "Karur", "Udhagamandalam", "Hosur", "Kanchipuram", "Nagercoil",
  ]),
  st("Telangana", [
    c("Hyderabad", [
      "Banjara Hills", "Jubilee Hills", "Gachibowli", "Madhapur", "HITEC City",
      "Kondapur", "Kukatpally", "Miyapur", "Ameerpet", "Begumpet", "Somajiguda",
      "Secunderabad", "SR Nagar", "Yousufguda", "Nallagandla", "Manikonda",
      "Financial District", "Kokapet", "Narsingi", "Tellapur", "Bachupally",
      "Nizampet", "Uppal", "LB Nagar", "Dilsukhnagar", "Charminar", "Abids",
      "Punjagutta", "Mehdipatnam", "Attapur",
    ]),
    "Warangal", "Nizamabad", "Karimnagar", "Ramagundam", "Khammam",
    "Mahbubnagar", "Nalgonda", "Adilabad", "Suryapet", "Miryalaguda", "Siddipet",
  ]),
  st("Tripura", ["Agartala", "Udaipur", "Dharmanagar", "Kailashahar", "Belonia", "Ambassa"]),
  st("Uttar Pradesh", [
    c("Noida", [
      "Sector 15", "Sector 18", "Sector 62", "Sector 63", "Sector 76",
      "Sector 78", "Sector 137", "Sector 150", "Greater Noida West", "Alpha 1",
      "Beta 2", "Pari Chowk",
    ]),
    c("Ghaziabad", [
      "Indirapuram", "Vaishali", "Vasundhara", "Kaushambi", "Raj Nagar Extension",
      "Crossings Republik", "Sahibabad", "Loni",
    ]),
    "Lucknow", "Kanpur", "Agra", "Meerut", "Varanasi", "Prayagraj", "Bareilly",
    "Aligarh", "Moradabad", "Saharanpur", "Gorakhpur", "Firozabad", "Jhansi",
    "Muzaffarnagar", "Mathura", "Rampur", "Shahjahanpur", "Ayodhya",
  ]),
  st("Uttarakhand", ["Dehradun", "Haridwar", "Roorkee", "Haldwani", "Rudrapur", "Kashipur", "Rishikesh", "Nainital", "Almora", "Pithoragarh"]),
  st("West Bengal", [
    c("Kolkata", [
      "Park Street", "Salt Lake", "Rajarhat", "New Town", "Ballygunge",
      "Alipore", "New Alipore", "Behala", "Tollygunge", "Jadavpur",
      "Gariahat", "Dhakuria", "Rashbehari", "Bhowanipore", "Kalighat",
      "Chowringhee", "Esplanade", "Shyambazar", "Dum Dum", "Kasba",
      "EM Bypass", "Sealdah", "Howrah", "Newtown Action Area 1",
      "Action Area 2", "Action Area 3",
    ]),
    "Howrah", "Durgapur", "Asansol", "Siliguri", "Bardhaman", "Malda",
    "Kharagpur", "Haldia", "Berhampore", "Krishnanagar", "Bankura",
    "Purulia", "Darjeeling",
  ]),
  // Union Territories
  st("Andaman and Nicobar Islands", ["Port Blair", "Diglipur", "Rangat", "Mayabunder", "Havelock Island"]),
  st("Chandigarh", ["Chandigarh"]),
  st("Dadra and Nagar Haveli and Daman and Diu", ["Silvassa", "Daman", "Diu"]),
  {
    state: "Delhi",
    cities: [
      c("New Delhi", ["Connaught Place", "Karol Bagh", "Chanakyapuri", "Lajpat Nagar", "Saket", "Dwarka", "Rohini", "Vasant Kunj", "Greater Kailash", "Janakpuri", "Pitampura", "Mayur Vihar"]),
      c("Delhi"),
    ],
  },
  st("Jammu and Kashmir", ["Srinagar", "Jammu", "Anantnag", "Baramulla", "Kathua", "Udhampur", "Sopore", "Rajouri"]),
  st("Ladakh", ["Leh", "Kargil", "Nubra", "Zanskar"]),
  st("Lakshadweep", ["Kavaratti", "Agatti", "Amini", "Andrott"]),
  st("Puducherry", ["Puducherry", "Karaikal", "Yanam", "Mahe"]),
];

// Backward-compat flat list (used by legacy filters). Prefer INDIA_LOCATIONS going forward.
export const MANGALORE_LOCATIONS = (() => {
  const out = [];
  INDIA_LOCATIONS.forEach((s) => s.cities.forEach((c) => {
    out.push(`${c.name}`);
    c.localities.forEach((l) => out.push(`${l}, ${c.name}`));
  }));
  return Array.from(new Set(out));
})();

export const AVAILABILITY = [
  { value: "available", label: "Available" },
  { value: "under_offer", label: "Under Offer" },
  { value: "sold_out", label: "Sold / Rented Out" },
];

export const CONTACT_PREFERENCES = [
  { value: "platform", label: "AcresDeal handles enquiries", hint: "Recommended · we screen and forward serious buyers" },
  { value: "direct", label: "Show my direct contact", hint: "Owner/broker phone + WhatsApp visible to buyers" },
  { value: "both", label: "Show both", hint: "AcresDeal + owner/broker contact both visible" },
];

export const LEAD_STATUSES = [
  { value: "new", label: "New", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "contacted", label: "Contacted", color: "bg-purple-50 text-purple-700 border-purple-200" },
  { value: "properties_shared", label: "Properties Shared", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { value: "site_visit", label: "Site Visit", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "negotiation", label: "Negotiation", color: "bg-orange-50 text-orange-700 border-orange-200" },
  { value: "closed", label: "Closed (Won)", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "cancelled", label: "Cancelled", color: "bg-slate-100 text-slate-700 border-slate-200" },
];

export const LEAD_SOURCES = [
  { value: "website_search", label: "Website Search" },
  { value: "property_listing", label: "Property Listing" },
  { value: "post_requirement", label: "Post Your Requirement" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "phone", label: "Phone" },
  { value: "broker_referral", label: "Broker Referral" },
  { value: "featured_listing", label: "Featured Listing" },
];

export const REQUIREMENT_PURPOSES = [
  "Personal use / Self-occupation",
  "Investment",
  "Business / Commercial use",
  "Farming / Agricultural",
  "Warehousing / Logistics",
  "Rental income",
  "Other",
];

export const ENQUIRY_ACTIONS = [
  { value: "call", label: "Call" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "site_visit", label: "Request Site Visit" },
  { value: "enquire", label: "Enquire" },
];
